import { normalizeStoryUserId } from '../api/socialStoriesApi';
import type { SocialPostUserGroup } from './socialPostsStorage';
import {
  buildStoryInitialsAvatarSrc,
  resolveStoryGroupAvatarSrc,
} from './profileAvatarDisplay';
import {
  readMessagingPeerDisplay,
  rememberMessagingPeerDisplay,
} from './messagingPeerCache';

export type MessagingPeerDisplay = {
  label: string;
  avatarSrc: string;
};

export type MessagingPeerHints = {
  peerLabel?: string | null;
  peerAvatarSrc?: string | null;
};

/** Resolve peer label/avatar: API hints → stories → local cache → fallback. */
export function resolveMessagingPeerDisplay(
  peerUserId: string,
  groups: readonly SocialPostUserGroup[],
  fallbackLabel: string,
  hints?: MessagingPeerHints | null,
): MessagingPeerDisplay {
  const peer = normalizeStoryUserId(peerUserId);
  const hintLabel = hints?.peerLabel?.trim() || '';
  const hintAvatar = hints?.peerAvatarSrc?.trim() || '';
  if (hintLabel) {
    const avatarSrc = hintAvatar || buildStoryInitialsAvatarSrc(hintLabel);
    rememberMessagingPeerDisplay(peerUserId, { label: hintLabel, avatarSrc });
    return { label: hintLabel, avatarSrc };
  }

  const match = groups.find((group) => normalizeStoryUserId(group.userId) === peer);
  if (match) {
    const label = match.userLabel.trim() || fallbackLabel;
    const avatarSrc =
      resolveStoryGroupAvatarSrc(match) ?? buildStoryInitialsAvatarSrc(label);
    rememberMessagingPeerDisplay(peerUserId, { label, avatarSrc });
    return { label, avatarSrc };
  }

  const cached = readMessagingPeerDisplay(peerUserId);
  if (cached?.label) {
    return {
      label: cached.label,
      avatarSrc: cached.avatarSrc || buildStoryInitialsAvatarSrc(cached.label),
    };
  }

  const fromId = peerUserId.trim();
  const label =
    fromId.includes('@') && fromId.length > 3 ? fromId : fallbackLabel;
  return {
    label,
    avatarSrc: buildStoryInitialsAvatarSrc(label),
  };
}

export function formatMessageTime(epochMs: number, locale: string): string {
  if (!epochMs) {
    return '';
  }
  const date = new Date(epochMs);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const tag = locale === 'he' ? 'he-IL' : 'en-GB';
  const now = Date.now();
  const sameDay =
    date.getFullYear() === new Date(now).getFullYear() &&
    date.getMonth() === new Date(now).getMonth() &&
    date.getDate() === new Date(now).getDate();
  try {
    if (sameDay) {
      return new Intl.DateTimeFormat(tag, { timeStyle: 'short' }).format(date);
    }
    return new Intl.DateTimeFormat(tag, {
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(date);
  } catch {
    return date.toISOString();
  }
}
